import * as React from "react";
import { ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";
import { cn } from "../../lib/utils";
import { SearchField } from "./Filters";
import { Table, TBody, TD, TH, THead, TR } from "./Table";
import { EmptyStateRow, type EmptyStateRowProps } from "./TableValue";
import { useSurface, type SurfaceKind } from "./Surface";

/**
 * A table that sits INSIDE something — a card, a drawer, a section of a record.
 *
 * Two things it decides so no author has to:
 *
 * 1. 🔴 HEADER VARIANT, BY CONTEXT. Full onyx at page level; a lighter tinted
 *    header inside a card or a drawer. Resolved through {@link useSurface}, so
 *    a section moved from a page into a drawer changes its own chrome. The
 *    `surface` prop exists only as an escape hatch and is almost always wrong.
 *
 * 2. 🔴 PROGRESSIVE CHROME. Below the threshold (ten rows) this renders the
 *    styled table and NOTHING ELSE — no search box, no sort affordances, no
 *    pagination footer. Above it, all three appear together. A search field
 *    over six rows is furniture that costs a line of vertical space and answers
 *    a question the reader can answer faster by looking; sort arrows on a
 *    four-row table are decoration that invites a click that changes nothing
 *    worth changing. Making that a per-table judgement guarantees twenty
 *    tables make it twenty ways, so it is made here, once, from the only fact
 *    that matters: how many rows there are.
 *
 * The threshold is deliberately about the UNFILTERED row count. Searching down
 * to three rows must not make the search box that got you there disappear.
 */

export interface SubTableColumn<T> {
  key: string;
  /**
   * The column heading.
   *
   * `ReactNode` rather than `string` because a header cell is sometimes a
   * CONTROL — a select-all checkbox is the standing example, and it belongs in
   * the header of the column it selects rather than floating above the table
   * where its relationship to the column is something the reader has to infer.
   * Both of this file's uses render it, neither reads it as text.
   */
  header: React.ReactNode;
  render: (row: T) => React.ReactNode;
  className?: string;
  /** Right-align numerics. Applied to header and cells together. */
  align?: "left" | "right";
  /** Makes the column sortable — but only once the table has earned chrome. */
  sortAccessor?: (row: T) => string | number | Date | null | undefined;
  /** Text this column contributes to search. Defaults to its rendered text. */
  searchText?: (row: T) => string;
}

export interface SubTableProps<T> {
  columns: SubTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Accessible name for the table. */
  caption: string;
  /** What to show when there are no rows at all. */
  empty?: Omit<EmptyStateRowProps, "colSpan">;
  /** Escape hatch. Leave it alone — the surface is meant to resolve itself. */
  surface?: SurfaceKind;
  /** Row count above which search, sort and the footer appear. */
  chromeThreshold?: number;
  /** Rows per page once the footer is showing. Defaults to the threshold. */
  pageSize?: number;
  searchPlaceholder?: string;
  /**
   * Replaces the default search (the whole query as one substring of any
   * column's text) for a table with a rule of its own — the Associated
   * listings tab's word-by-word listing-agent search (Addendum B). Called
   * with the trimmed query; never called with a blank one.
   */
  matchRow?: (row: T, query: string) => boolean;
  /**
   * Makes the ROW the control.
   *
   * 🔴 A clickable `<tr>` is only half a control until it can be reached from
   * the keyboard: a `<tr>` is not in the tab order and does not respond to
   * Enter or Space, so a mouse-only row is invisible to anyone driving the page
   * without one. Supplying this therefore also gives the row `role="button"`,
   * `tabIndex={0}` and Enter/Space activation — that is not optional decoration
   * a caller can forget, which is exactly why it lives here rather than at each
   * call site.
   */
  onRowClick?: (row: T) => void;
  /**
   * Which row is currently being read, when the table drives a detail pane
   * beside it. Marks the row `aria-current` as well as tinting it — a
   * highlight alone says nothing to a screen reader, and "which one am I
   * looking at" is the entire question a master-detail pair exists to answer.
   */
  rowSelected?: (row: T) => boolean;
  /**
   * A drill-in rendered UNDERNEATH its row, spanning the whole table.
   *
   * For a count that opens into the thing it counted — a specialist's book, an
   * invoice's lines — where the detail belongs to one row and only one row is
   * ever open. Return `null` to render nothing, which is what a closed row
   * does.
   *
   * 🔴 The `colSpan` is computed from `columns`, deliberately. Hand-written
   * span counts are a silent breakage waiting for the next column: nothing
   * fails, the cell just stops reaching the end of the table.
   */
  renderExpanded?: (row: T) => React.ReactNode;
  rowTestId?: (row: T) => string;
  /**
   * Pin the header while the body scrolls. Forwarded to `Table`, which caps
   * the wrapper's height and makes the header cells sticky.
   *
   * 🔴 `Table`'s sticky rule paints the pinned cells `bg-onyx`, which is the
   * PAGE variant. On a card or drawer surface the tinted header must override
   * it rather than inherit it (§4.3), so the surface class is applied to the
   * cells as well as the `<thead>` when this is set.
   */
  stickyHeader?: boolean;
  /**
   * Column keys to leave out — the reader's column choices, typically held by
   * a `ColumnChooser` in the page's toolbar and persisted per user. Filtered
   * here so the `colSpan` of an expanded row and the empty-state row still
   * count the columns actually rendered.
   */
  hiddenColumnKeys?: readonly string[];
  /**
   * Reports the rows on screen — after search, sort and paging — whenever WHICH
   * rows they are changes. For a header control that acts on "this page" (a
   * select-all), which only the table can know once it pages client-side.
   *
   * 🔴 Called once per change of the rendered `rowKey` sequence, not once per
   * render: the slice is a new array every render, and a caller that sets state
   * from it would otherwise loop. A row whose CONTENT changes under the same key
   * is not reported again — read the row's current data by key, not from the
   * array this hands you.
   */
  onPageRowsChange?: (rows: T[]) => void;
  className?: string;
  "data-testid"?: string;
}

export const SUB_TABLE_CHROME_THRESHOLD = 10;

function nodeToText(node: React.ReactNode): string {
  if (node == null || node === false || node === true) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join(" ");
  if (typeof node === "object" && "props" in node) {
    return nodeToText((node as { props: { children?: React.ReactNode } }).props.children);
  }
  return "";
}

function compare(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

/** Page-level header is the screen's own furniture; anywhere else it is a guest. */
const HEAD_CLASS: Record<SurfaceKind, string> = {
  page: "bg-onyx",
  card: "bg-white-warm border-b border-border-light",
  drawer: "bg-white-warm border-b border-border-light",
};

/**
 * `text-white-pure`, not the `text-gold` the shared `TH` still defaults to.
 * Gold on onyx is 4.0:1 as a 10px label — the shipped default predates the
 * standard and is the thing being migrated, not the authority. Overriding it
 * here rather than in `TH` keeps the change to tables that opt in; sweeping
 * the base would restyle every table in the app in a primitives PR.
 */
const TH_CLASS: Record<SurfaceKind, string> = {
  page: "text-white-pure",
  card: "text-gray-mid",
  drawer: "text-gray-mid",
};

/** What a PINNED header cell paints, per surface — `Table`'s sticky rule sets
 *  `bg-onyx` on the cell, so the tinted variants must say otherwise there too. */
const STICKY_TH_CLASS: Record<SurfaceKind, string> = {
  page: "",
  card: "!bg-white-warm",
  drawer: "!bg-white-warm",
};

export function SubTable<T>({
  columns: allColumns,
  rows,
  rowKey,
  caption,
  empty,
  surface,
  chromeThreshold = SUB_TABLE_CHROME_THRESHOLD,
  pageSize,
  searchPlaceholder,
  matchRow,
  onRowClick,
  rowSelected,
  renderExpanded,
  rowTestId,
  stickyHeader,
  hiddenColumnKeys,
  onPageRowsChange,
  className,
  "data-testid": testId,
}: SubTableProps<T>) {
  const columns = React.useMemo(
    () =>
      hiddenColumnKeys && hiddenColumnKeys.length > 0
        ? allColumns.filter((c) => !hiddenColumnKeys.includes(c.key))
        : allColumns,
    [allColumns, hiddenColumnKeys],
  );
  const rootRef = React.useRef<HTMLDivElement>(null);
  const resolvedSurface = useSurface(rootRef, surface);

  const [query, setQuery] = React.useState("");
  const [sort, setSort] = React.useState<{ key: string; dir: "asc" | "desc" } | null>(null);
  const [page, setPage] = React.useState(0);

  // Keyed on the UNFILTERED count, so searching down to three rows cannot pull
  // the search box out from under the reader who is typing in it.
  const hasChrome = rows.length > chromeThreshold;
  const perPage = pageSize ?? chromeThreshold;

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!hasChrome || !q) return rows;
    if (matchRow) return rows.filter((row) => matchRow(row, query.trim()));
    return rows.filter((row) =>
      columns.some((col) => {
        const text = col.searchText ? col.searchText(row) : nodeToText(col.render(row));
        return text.toLowerCase().includes(q);
      }),
    );
  }, [rows, columns, query, hasChrome, matchRow]);

  const sorted = React.useMemo(() => {
    if (!hasChrome || !sort) return filtered;
    const col = columns.find((c) => c.key === sort.key && c.sortAccessor);
    if (!col?.sortAccessor) return filtered;
    const accessor = col.sortAccessor;
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => compare(accessor(a), accessor(b)) * factor);
  }, [filtered, columns, sort, hasChrome]);

  const total = sorted.length;
  const totalPages = hasChrome ? Math.max(1, Math.ceil(total / perPage)) : 1;
  const currentPage = Math.min(page, totalPages - 1);
  const visible = hasChrome
    ? sorted.slice(currentPage * perPage, currentPage * perPage + perPage)
    : sorted;

  // Keyed on the rendered ids, so a fresh slice of the same rows is no change.
  // The latest rows and callback are read through a ref the layout effect fills
  // (it runs before the passive effect below), so neither has to be a dependency.
  const visibleKey = onPageRowsChange ? visible.map(rowKey).join("\u0000") : "";
  const pageRowsLatest = React.useRef({ visible, onPageRowsChange });
  React.useLayoutEffect(() => {
    pageRowsLatest.current = { visible, onPageRowsChange };
  });
  React.useEffect(() => {
    const { visible: shown, onPageRowsChange: report } = pageRowsLatest.current;
    report?.(shown);
  }, [visibleKey]);

  // A narrowed result set can leave the reader stranded on page four of one.
  React.useEffect(() => {
    setPage(0);
  }, [query]);

  const toggleSort = (col: SubTableColumn<T>) => {
    if (!hasChrome || !col.sortAccessor) return;
    setSort((prev) => {
      if (!prev || prev.key !== col.key) return { key: col.key, dir: "asc" };
      return prev.dir === "asc" ? { key: col.key, dir: "desc" } : null;
    });
  };

  const firstShown = total === 0 ? 0 : currentPage * perPage + 1;
  const lastShown = Math.min((currentPage + 1) * perPage, total);

  return (
    <div ref={rootRef} className={cn("flex min-w-0 flex-col gap-space-3", className)}>
      {hasChrome && (
        <SearchField
          value={query}
          onChange={setQuery}
          placeholder={searchPlaceholder ?? `Search ${caption.toLowerCase()}…`}
          label={`Search ${caption}`}
          className="max-w-sm"
        />
      )}

      <Table
        data-testid={testId}
        data-phx-surface-resolved={resolvedSurface}
        stickyHeader={stickyHeader}
      >
        <caption className="sr-only">{caption}</caption>
        <THead className={HEAD_CLASS[resolvedSurface]}>
          <TR className="border-t-0">
            {columns.map((col) => {
              const sortable = hasChrome && !!col.sortAccessor;
              const active = sort?.key === col.key;
              return (
                <TH
                  key={col.key}
                  className={cn(
                    TH_CLASS[resolvedSurface],
                    stickyHeader && STICKY_TH_CLASS[resolvedSurface],
                    col.align === "right" && "text-right",
                    col.className,
                  )}
                  aria-sort={
                    active ? (sort?.dir === "asc" ? "ascending" : "descending") : undefined
                  }
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(col)}
                      className="inline-flex items-center gap-1 focus-visible:outline-none focus-visible:underline"
                      data-testid={`subtable-sort-${col.key}`}
                    >
                      {col.header}
                      {active && sort?.dir === "asc" ? (
                        <ChevronUp size={12} strokeWidth={2} aria-hidden />
                      ) : active ? (
                        <ChevronDown size={12} strokeWidth={2} aria-hidden />
                      ) : (
                        <ChevronsUpDown size={12} strokeWidth={2} aria-hidden className="opacity-50" />
                      )}
                    </button>
                  ) : (
                    col.header
                  )}
                </TH>
              );
            })}
          </TR>
        </THead>
        <TBody>
          {visible.length === 0 ? (
            <EmptyStateRow
              colSpan={columns.length}
              message={
                query.trim()
                  ? `Nothing matches “${query.trim()}”.`
                  : (empty?.message ?? "Nothing here yet.")
              }
              // A no-results view offers no door — the door creates a record,
              // and the reader's problem is their filter, not a missing one.
              door={query.trim() ? undefined : empty?.door}
              data-testid={empty?.["data-testid"]}
            />
          ) : (
            visible.map((row) => {
              const selected = rowSelected?.(row) ?? false;
              const expanded = renderExpanded?.(row) ?? null;
              return (
              <React.Fragment key={rowKey(row)}>
              <TR
                className={cn(
                  onRowClick && "cursor-pointer hover:bg-white-warm",
                  selected && "bg-gold/10",
                )}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                // The keyboard half of "the row is the control". Without these
                // the row is a mouse-only affordance — see `onRowClick`.
                role={onRowClick ? "button" : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                aria-current={selected ? "true" : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          // Space scrolls the page otherwise, which moves the
                          // table out from under the row just activated.
                          e.preventDefault();
                          onRowClick(row);
                        }
                      }
                    : undefined
                }
                data-testid={rowTestId?.(row)}
              >
                {columns.map((col) => (
                  <TD
                    key={col.key}
                    className={cn(col.align === "right" && "text-right tabular-nums", col.className)}
                  >
                    {col.render(row)}
                  </TD>
                ))}
              </TR>
              {expanded != null && (
                <TR key={`${rowKey(row)}:expanded`} className="bg-white-warm">
                  <TD colSpan={columns.length} className="p-space-3">
                    {expanded}
                  </TD>
                </TR>
              )}
              </React.Fragment>
              );
            })
          )}
        </TBody>
      </Table>

      {hasChrome && (
        <SubTableFooter
          firstShown={firstShown}
          lastShown={lastShown}
          total={total}
          page={currentPage + 1}
          totalPages={totalPages}
          onPrevious={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
        />
      )}
    </div>
  );
}

export interface SubTableFooterProps {
  firstShown: number;
  lastShown: number;
  total: number;
  page: number;
  totalPages: number;
  onPrevious: () => void;
  onNext: () => void;
  className?: string;
  /**
   * Opt-in page-size chooser (Table & Tile v1.5). When BOTH this and
   * {@link onPageSizeChange} are supplied, a small `<select>` renders beside the
   * page counter; when either is absent the footer renders exactly as it always
   * has, so no existing consumer changes.
   *
   * <p>The footer only REPORTS the choice. The caller owns the page size and
   * decides what a change does to the page number — a list that pages on the
   * server normally returns to page 1, because row 26 of a 25-row page is row 1
   * of nothing.</p>
   */
  pageSizeOptions?: readonly number[];
  /** The page size currently in force. Required for the chooser to show a value. */
  pageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
}

/**
 * `Showing X–Y of Z · Page N of M` on the left, PREVIOUS / NEXT on the right.
 *
 * Both halves of the readout are load-bearing and neither replaces the other:
 * the range answers "have I seen everything", the page answers "how much is
 * left". Exported so a table that cannot use `SubTable` still gets the same
 * footer rather than inventing a third one.
 */
export function SubTableFooter({
  firstShown,
  lastShown,
  total,
  page,
  totalPages,
  onPrevious,
  onNext,
  className,
  pageSizeOptions,
  pageSize,
  onPageSizeChange,
}: SubTableFooterProps) {
  const counter = (
    <span className="tabular-nums">
      Showing {firstShown}–{lastShown} of {total} · Page {page} of {totalPages}
    </span>
  );
  // Default-off: with no options (or no handler) the counter renders on its own,
  // byte-identical to the footer before the chooser existed.
  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-space-2 text-caption text-gray-mid",
        className,
      )}
      data-testid="subtable-footer"
    >
      {pageSizeOptions != null && pageSizeOptions.length > 0 && onPageSizeChange != null ? (
        <span className="flex flex-wrap items-center gap-space-3">
          {counter}
          <label className="inline-flex items-center gap-space-1">
            <span>Per page</span>
            <select
              value={pageSize ?? pageSizeOptions[0]}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="rounded-md border border-border-light bg-white-pure px-space-1 py-[2px] text-caption text-onyx focus-visible:outline-none focus-visible:shadow-goldRing"
              data-testid="subtable-page-size"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        </span>
      ) : (
        counter
      )}
      <span className="flex items-center gap-space-2">
        <PagerButton onClick={onPrevious} disabled={page <= 1} testId="subtable-prev">
          Previous
        </PagerButton>
        <PagerButton onClick={onNext} disabled={page >= totalPages} testId="subtable-next">
          Next
        </PagerButton>
      </span>
    </div>
  );
}

function PagerButton({
  onClick,
  disabled,
  testId,
  children,
}: {
  onClick: () => void;
  disabled: boolean;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "rounded-md border border-border-light px-space-2 py-[3px]",
        "font-display text-[10px] font-bold uppercase tracking-label text-onyx",
        "transition-colors duration-fast ease-standard hover:border-gold",
        "disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-border-light",
      )}
      data-testid={testId}
    >
      {children}
    </button>
  );
}
